import { forwardRef, ButtonHTMLAttributes, useState, useRef } from 'react';
import { createPortal } from 'react-dom';
import classNames from 'classnames';

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: 'primary' | 'outline' | 'ghost' | 'success' | 'warning' | 'error';
  size?: 'sm' | 'md' | 'lg' | 'xl';
  fullWidth?: boolean;
  loading?: boolean;
  active?: boolean;
  icon?: React.ReactNode;
  leftIcon?: React.ReactNode;
  rightIcon?: React.ReactNode;
  tooltip?: string;
  tooltipPosition?: 'top' | 'bottom' | 'left' | 'right';
}

/**
 * Unified Button component - Supports both text and icon-only buttons
 *
 * Follows standard design system hierarchy:
 * - primary: Main action (teal filled)
 * - outline: Default/cancel/secondary (white with border)
 * - ghost: Subtle actions, icon buttons, modal close
 * - success/warning/error: Semantic actions
 *
 * Features:
 * - Icon-only mode: Provide `icon` without `children`
 * - Text with icon mode: Provide `icon` with `children` (icon becomes leftIcon)
 * - Loading state with spinner
 * - Active state for toolbar buttons
 * - Desktop-only tooltip support
 * - Full accessibility support
 */
export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      variant = 'primary',
      size = 'md',
      fullWidth = false,
      loading = false,
      active = false,
      icon,
      leftIcon,
      rightIcon,
      tooltip,
      tooltipPosition = 'top',
      children,
      className,
      disabled,
      ...props
    },
    ref
  ) => {
    const [showTooltip, setShowTooltip] = useState(false);
    const [tooltipPos, setTooltipPos] = useState<{
      top: number;
      left: number;
    } | null>(null);
    const buttonRef = useRef<HTMLButtonElement>(null);

    // Determine if this is an icon-only button
    const isIconOnly = !!icon && !children && !leftIcon && !rightIcon;

    // Determine effective icons
    const effectiveLeftIcon = leftIcon || (isIconOnly ? null : icon);
    const effectiveIcon = isIconOnly ? icon : null;

    const baseStyles =
      'inline-flex items-center justify-center font-medium transition-all duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-2 disabled:opacity-50 disabled:pointer-events-none';

    const variantStyles = {
      primary:
        'bg-primary-500 text-white hover:bg-primary-600 active:bg-primary-700 shadow-sm dark:bg-primary-600 dark:hover:bg-primary-700',
      outline:
        'border border-gray-300 dark:border-gray-500 bg-white dark:bg-gray-800 text-foreground hover:bg-gray-50 dark:hover:bg-gray-700 hover:border-gray-400 dark:hover:border-gray-400 shadow-sm',
      ghost:
        'bg-transparent hover:bg-neutral-100 active:bg-neutral-200 dark:hover:bg-neutral-800 dark:active:bg-neutral-700 text-neutral-700 dark:text-neutral-300',
      success:
        'bg-success-500 text-white hover:bg-success-600 active:bg-success-700 shadow-sm dark:bg-success-600 dark:hover:bg-success-700',
      warning:
        'bg-warning-500 text-white hover:bg-warning-600 active:bg-warning-700 shadow-sm dark:bg-warning-600 dark:hover:bg-warning-700',
      error:
        'bg-error-500 text-white hover:bg-error-600 active:bg-error-700 shadow-sm dark:bg-error-600 dark:hover:bg-error-700',
    };

    const activeStyles = {
      primary: 'bg-primary-700 dark:bg-primary-800',
      outline:
        'bg-primary-50 dark:bg-primary-900/20 border-primary-500 text-primary-700 dark:text-primary-400',
      ghost:
        'bg-primary-100 dark:bg-primary-900/30 text-primary-700 dark:text-primary-400',
      success: 'bg-success-700 dark:bg-success-800',
      warning: 'bg-warning-700 dark:bg-warning-800',
      error: 'bg-error-700 dark:bg-error-800',
    };

    const sizeStyles = isIconOnly
      ? {
          sm: 'h-8 w-8 p-0 rounded-md',
          md: 'h-10 w-10 p-0 rounded-lg',
          lg: 'h-12 w-12 p-0 rounded-lg',
          xl: 'h-14 w-14 p-0 rounded-xl',
        }
      : {
          sm: 'h-8 px-3 text-sm rounded-md gap-1.5',
          md: 'h-10 px-4 text-base rounded-md gap-2',
          lg: 'h-12 px-6 text-lg rounded-lg gap-2',
          xl: 'h-14 px-8 text-xl rounded-lg gap-2.5',
        };

    const buttonClasses = classNames(
      baseStyles,
      active ? activeStyles[variant] : variantStyles[variant],
      sizeStyles[size],
      fullWidth && 'w-full',
      className
    );

    // Tooltip handlers (desktop only)
    const handleMouseEnter = () => {
      if (tooltip && buttonRef.current) {
        const rect = buttonRef.current.getBoundingClientRect();
        const scrollTop =
          window.pageYOffset || document.documentElement.scrollTop;
        const scrollLeft =
          window.pageXOffset || document.documentElement.scrollLeft;

        let top = 0;
        let left = 0;

        switch (tooltipPosition) {
          case 'top':
            top = rect.top + scrollTop - 8;
            left = rect.left + scrollLeft + rect.width / 2;
            break;
          case 'bottom':
            top = rect.bottom + scrollTop + 8;
            left = rect.left + scrollLeft + rect.width / 2;
            break;
          case 'left':
            top = rect.top + scrollTop + rect.height / 2;
            left = rect.left + scrollLeft - 8;
            break;
          case 'right':
            top = rect.top + scrollTop + rect.height / 2;
            left = rect.right + scrollLeft + 8;
            break;
        }

        setTooltipPos({ top, left });
        setShowTooltip(true);
      }
    };

    const handleMouseLeave = () => {
      setShowTooltip(false);
      setTooltipPos(null);
    };

    const positionClasses = {
      top: '-translate-x-1/2 -translate-y-full',
      bottom: '-translate-x-1/2 translate-y-0',
      left: '-translate-y-1/2 -translate-x-full',
      right: '-translate-y-1/2 translate-x-0',
    };

    const buttonElement = (
      <button
        ref={(node) => {
          // Handle both refs
          if (typeof ref === 'function') {
            ref(node);
          } else if (ref) {
            ref.current = node;
          }
          (
            buttonRef as React.MutableRefObject<HTMLButtonElement | null>
          ).current = node;
        }}
        className={buttonClasses}
        disabled={disabled || loading}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        aria-label={
          isIconOnly && !props['aria-label'] ? tooltip : props['aria-label']
        }
        {...props}
      >
        {loading && (
          <svg
            className="animate-spin h-4 w-4"
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
          >
            <circle
              className="opacity-25"
              cx="12"
              cy="12"
              r="10"
              stroke="currentColor"
              strokeWidth="4"
            />
            <path
              className="opacity-75"
              fill="currentColor"
              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
            />
          </svg>
        )}
        {!loading && effectiveLeftIcon && (
          <span className="flex-shrink-0">{effectiveLeftIcon}</span>
        )}
        {!loading && effectiveIcon && (
          <span className="flex items-center justify-center w-full h-full">
            {effectiveIcon}
          </span>
        )}
        {children && <span>{children}</span>}
        {!loading && rightIcon && (
          <span className="flex-shrink-0">{rightIcon}</span>
        )}
      </button>
    );

    return (
      <>
        {buttonElement}
        {/* Desktop-only tooltip */}
        {tooltip &&
          showTooltip &&
          tooltipPos &&
          typeof document !== 'undefined' &&
          createPortal(
            <div
              role="tooltip"
              className={classNames(
                'absolute z-50 px-3 py-2 text-sm font-medium text-white bg-gray-900 dark:bg-gray-700 rounded-lg shadow-lg',
                'max-w-xs break-words',
                'hidden md:block',
                'pointer-events-none',
                positionClasses[tooltipPosition]
              )}
              style={{
                top: `${tooltipPos.top}px`,
                left: `${tooltipPos.left}px`,
              }}
            >
              {tooltip}
              {/* Arrow */}
              <div
                className={classNames(
                  'absolute w-2 h-2 bg-gray-900 dark:bg-gray-700 rotate-45',
                  {
                    'bottom-[-4px] left-1/2 -translate-x-1/2':
                      tooltipPosition === 'top',
                    'top-[-4px] left-1/2 -translate-x-1/2':
                      tooltipPosition === 'bottom',
                    'right-[-4px] top-1/2 -translate-y-1/2':
                      tooltipPosition === 'left',
                    'left-[-4px] top-1/2 -translate-y-1/2':
                      tooltipPosition === 'right',
                  }
                )}
              />
            </div>,
            document.body
          )}
      </>
    );
  }
);

Button.displayName = 'Button';
